import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Info, RefreshCw, Search, Target, X } from 'lucide-react';
import { apiClient } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { errMessage, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle, monoLabel } from '../components/UI/ink';

// Baselines: agents on the left, the selected agent's baseline and drift on
// the right (same pattern as Network Map and Security Alerts). Every state is
// explicit — "waiting for snapshot" and "expired" never pass as clean or
// quietly turn into "not set".
const WINDOW_DAYS = 30;
const SOON_DAYS = 5;
const FILTERS = [['all', 'All'], ['drift', 'Drifting'], ['clean', 'Clean'], ['needs', 'Needs baseline'], ['waiting', 'Waiting for snapshot']];
const groupOf = (s) => (s === 'none' || s === 'expired' ? 'needs' : s);
const KIND_ORDER = { install: 0, remove: 1, downgrade: 2, upgrade: 3 };

const parseTs = (iso) => {
  if (!iso) return null;
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};
const daysLeft = (iso) => { const d = parseTs(iso); return d ? Math.ceil((d.getTime() - Date.now()) / 86400000) : null; };
const ago = (iso) => {
  const d = parseTs(iso);
  if (!d) return null;
  const m = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};
const stamp = (iso, opts = {}) => {
  const d = parseTs(iso);
  return d ? d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur', ...opts }) : '—';
};
const isOnline = (a) => { const d = parseTs(a.last_seen); return !!d && Date.now() - d.getTime() < 15 * 60000; };

const seg = (on) => ({
  background: on ? 'var(--surf-raised)' : 'transparent',
  borderColor: on ? 'var(--surf-line)' : 'transparent',
  color: on ? 'var(--ink-text)' : 'var(--ink-muted)',
});

const Chip = ({ k, children }) => (
  <span className="inline-flex items-center gap-1.5 h-[22px] px-[7px] rounded-md font-mono text-[10.5px] font-medium tracking-[0.04em] shrink-0" style={{ background: `var(--sev-${k}-bg)` }}>
    <span className="w-1.5 h-1.5 rounded-[1px]" style={{ background: `var(--sev-${k}-fg)` }} />{children}
  </span>
);

const Dot = ({ color, dashed }) => (
  <span className="w-2 h-2 box-border rounded-full shrink-0" style={{ background: color || 'transparent', border: color ? 'none' : `1px ${dashed ? 'dashed' : 'solid'} var(--ink-faint)` }} />
);

// Where a baseline sits in its 30-day window.
const windowOf = (a) => {
  const left = daysLeft(a.baseline_expires_at);
  if (a.status === 'none' || left == null) return { left: null, pct: 0, color: 'var(--surf-raised)' };
  if (left <= 0) return { left, pct: 100, color: 'var(--surf-line-strong)' };
  return { left, pct: Math.min(100, Math.max(0, ((WINDOW_DAYS - left) / WINDOW_DAYS) * 100)), color: left <= SOON_DAYS ? 'var(--sev-med-fg)' : 'var(--ink-muted)' };
};

const WindowBar = ({ w, width = 140, height = 4 }) => (
  <span className="flex rounded-sm" style={{ width, height, background: 'var(--surf-raised)' }}>
    <span className="rounded-sm" style={{ width: `${w.pct}%`, height, background: w.color }} />
  </span>
);

const BaselineManagement = () => {
  const { permissions } = useAuth();
  const canEdit = (permissions || []).includes('assets:update');
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [discovered, setDiscovered] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(null); // server id being set, or 'bulk'

  const selectedId = searchParams.get('agent');
  const select = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('agent', id); else next.delete('agent');
    setSearchParams(next, { replace: true });
    setConfirming(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/baselines');
      setAgents(res.data.baselines || []);
      setDiscovered(res.data.discovered_count || 0);
    } catch (err) {
      setNotice({ kind: 'error', text: errMessage(err, 'Failed to load baselines.') });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const counts = useMemo(() => {
    const c = { all: agents.length, drift: 0, clean: 0, needs: 0, waiting: 0 };
    agents.forEach(a => { c[groupOf(a.status)] += 1; });
    return c;
  }, [agents]);
  const expiringSoon = agents.filter(a => ['drift', 'clean', 'waiting'].includes(a.status) && (daysLeft(a.baseline_expires_at) ?? 99) <= 7).length;
  const missing = agents.filter(a => groupOf(a.status) === 'needs' && a.last_snapshot_at);
  const rows = agents.filter(a =>
    (filter === 'all' || groupOf(a.status) === filter)
    && (!q || a.hostname?.toLowerCase().includes(q) || (a.ip_address || '').includes(q)));
  const selected = agents.find(a => a.server_id === selectedId) || null;

  const setBaseline = async (a) => {
    const res = await apiClient.post(`/api/drift/${a.server_id}/baseline`);
    return res.data;
  };

  const runSelected = async () => {
    if (!selected) return;
    setBusy(selected.server_id);
    try {
      const r = await setBaseline(selected);
      setNotice({ kind: 'ok', text: `Baseline for ${selected.hostname} set from ${r.package_count} packages.` });
      setConfirming(false);
      await load();
    } catch (err) {
      setNotice({ kind: 'error', text: `${selected.hostname}: ${errMessage(err, 'Failed to set baseline.')}` });
    } finally {
      setBusy(null);
    }
  };

  const runMissing = async () => {
    setBusy('bulk');
    const failures = [];
    for (const a of missing) {
      try { await setBaseline(a); } catch (err) { failures.push(`${a.hostname}: ${errMessage(err, 'failed')}`); }
    }
    const ok = missing.length - failures.length;
    setNotice(failures.length
      ? { kind: 'error', text: `Set ${ok} of ${missing.length} baselines. ${failures.join(' · ')}` }
      : { kind: 'ok', text: `Set ${ok} baseline${ok === 1 ? '' : 's'}.` });
    setBusy(null);
    load();
  };

  const subtitle = loading && !agents.length
    ? 'Loading…'
    : [
        `${agents.length} agent${agents.length === 1 ? '' : 's'}`,
        `${counts.drift} drifting`,
        expiringSoon ? `${expiringSoon} baseline${expiringSoon === 1 ? '' : 's'} expire${expiringSoon === 1 ? 's' : ''} this week` : null,
        `${counts.needs} need${counts.needs === 1 ? 's' : ''} a baseline`,
      ].filter(Boolean).join(' · ');

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-[18px]" style={{ color: 'var(--ink-text)' }}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>ASSETS &amp; DEFENSE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Baseline Management</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <button type="button" onClick={load} className={secondaryBtn} style={secondaryBtnStyle}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.8} />Refresh
          </button>
          {canEdit && missing.length > 0 && (
            <button type="button" onClick={runMissing} disabled={!!busy} className={primaryBtn} style={primaryBtnStyle}>
              <Target className="w-4 h-4" strokeWidth={2} />{busy === 'bulk' ? 'Setting…' : `Set missing baselines (${missing.length})`}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-[240px] h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <Search className="w-[15px] h-[15px] shrink-0" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.8} />
          <input type="search" aria-label="Filter agents" placeholder="Hostname or IP" value={search} onChange={e => setSearch(e.target.value)} className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]" style={{ color: 'var(--ink-text)' }} />
        </div>
        <div role="group" aria-label="Baseline status" className="flex flex-wrap gap-1 p-[3px] rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          {FILTERS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setFilter(id)} aria-pressed={filter === id} className="h-8 flex items-center gap-1.5 px-[11px] rounded-[7px] border text-[13px]" style={seg(filter === id)}>
              {label}<span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{counts[id]}</span>
            </button>
          ))}
        </div>
      </div>

      {notice && (
        <div role={notice.kind === 'error' ? 'alert' : 'status'} className="flex items-start justify-between gap-3 px-4 py-3 rounded-[10px] border text-[13px]" style={{ borderColor: notice.kind === 'error' ? 'var(--sev-crit-fg)' : 'var(--sev-good-fg)', background: notice.kind === 'error' ? 'var(--sev-crit-bg)' : 'var(--sev-good-bg)' }}>
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="shrink-0" style={{ color: 'var(--ink-muted)' }}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_460px] gap-5 xl:h-[calc(100vh-290px)] xl:min-h-[560px]">
        <section aria-label="Agents" className="min-h-[420px] rounded-[14px] border flex flex-col overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <div className="flex-grow min-h-0 overflow-auto">
            <table className="w-full min-w-[640px] border-collapse table-fixed">
              <thead className="sticky top-0 z-[1]" style={{ background: 'var(--surf-surface)' }}>
                <tr className="h-[42px] border-b" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}>
                  <th scope="col" className={`px-4 text-left font-medium ${monoLabel}`}>AGENT</th>
                  <th scope="col" className={`w-[170px] px-2 text-left font-medium ${monoLabel}`}>BASELINE · 30-DAY WINDOW</th>
                  <th scope="col" className={`w-[72px] px-2 text-right font-medium ${monoLabel}`}>PKGS</th>
                  <th scope="col" className={`w-[176px] px-4 text-left font-medium ${monoLabel}`}>DRIFT</th>
                </tr>
              </thead>
              <tbody>
                {loading && !agents.length && <tr><td colSpan={4} className="h-32 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>Loading…</td></tr>}
                {!loading && !rows.length && (
                  <tr><td colSpan={4} className="h-32 px-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
                    {agents.length ? 'No agents match these filters.' : 'No agents registered yet. Install the agent from Agent Registry to start tracking baselines.'}
                  </td></tr>
                )}
                {rows.map(a => {
                  const on = a.server_id === selectedId;
                  const w = windowOf(a);
                  const soon = w.left != null && w.left > 0 && w.left <= SOON_DAYS;
                  const baseLabel = a.status === 'none' ? 'Not set'
                    : a.status === 'expired' ? `Expired ${Math.max(0, -w.left)} d ago`
                    : soon ? `Expires in ${w.left} d` : `${w.left} days left`;
                  const state = {
                    clean: [soon ? `Clean · expires in ${w.left} d` : 'No drift', 'var(--sev-good-fg)', false],
                    waiting: ['Waiting for snapshot', null, true],
                    expired: ['Expired — not checked', null, false],
                    none: ['No baseline', null, true],
                  }[a.status];
                  const pkgs = a.package_count || a.current_package_count;
                  return (
                    <tr key={a.server_id} className="h-16 border-b" style={{ borderColor: 'var(--surf-line)', background: on ? 'var(--surf-raised)' : 'transparent' }}>
                      <td className="px-4">
                        <button type="button" onClick={() => select(on ? null : a.server_id)} aria-pressed={on} className="flex flex-col gap-[3px] min-w-0 max-w-full text-left">
                          <span className="font-mono text-[13px] truncate">{a.hostname}</span>
                          <span className="text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{[a.ip_address, a.os_info].filter(Boolean).join(' · ') || '—'}</span>
                        </button>
                      </td>
                      <td className="px-2">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-[12.5px]" style={{ color: a.status === 'none' || a.status === 'expired' ? 'var(--ink-faint)' : soon ? 'var(--ink-text)' : 'var(--ink-muted)' }}>{baseLabel}</span>
                          <WindowBar w={w} />
                        </div>
                      </td>
                      <td className="px-2 text-right font-mono text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>{pkgs || '—'}</td>
                      <td className="px-4">
                        {a.status === 'drift' ? (
                          <span className="flex flex-wrap gap-1.5">
                            {a.drift.high > 0 && <Chip k="high">{a.drift.high} HIGH</Chip>}
                            {a.drift.medium > 0 && <Chip k="med">{a.drift.medium} MED</Chip>}
                          </span>
                        ) : (
                          <span className="flex items-center gap-[7px] text-[12.5px]" style={{ color: 'var(--ink-muted)' }}><Dot color={state[1]} dashed={state[2]} />{state[0]}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {discovered > 0 && (
            <div className="flex items-center gap-2.5 px-4 py-3.5 border-t text-[12.5px] shrink-0" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
              <Info className="w-[15px] h-[15px] shrink-0" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.8} />
              <span className="flex-grow">{discovered} discovered device{discovered === 1 ? ' isn’t' : 's aren’t'} listed — a baseline needs the agent’s package inventory.</span>
              <Link to="/network-map" className="py-1.5 shrink-0" style={{ color: 'var(--ink-link)' }}>Network Map</Link>
            </div>
          )}
        </section>

        <Details
          a={selected}
          canEdit={canEdit}
          busy={busy}
          confirming={confirming}
          onClose={() => select(null)}
          onPrimary={() => {
            if (!selected) return;
            // Replacing a live baseline discards it, so ask first.
            if (['drift', 'clean', 'waiting'].includes(selected.status)) setConfirming(true);
            else runSelected();
          }}
          onConfirm={runSelected}
          onCancel={() => setConfirming(false)}
        />
      </div>
    </div>
  );
};

const Details = ({ a, canEdit, busy, confirming, onClose, onPrimary, onConfirm, onCancel }) => {
  if (!a) {
    return (
      <aside aria-label="Baseline details" className="min-h-[420px] rounded-[14px] border flex flex-col items-center justify-center gap-2.5 p-8 text-center" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
        <span className="text-[15px] font-semibold">Select an agent</span>
        <span className="text-[13px] leading-normal max-w-[300px]" style={{ color: 'var(--ink-muted)' }}>Its baseline, what changed since, and whether to accept the changes show here.</span>
      </aside>
    );
  }

  const w = windowOf(a);
  const online = isOnline(a);
  const snapAgo = ago(a.last_snapshot_at);
  const devs = (a.drift?.deviations || []).slice().sort((x, y) => (x.severity === y.severity ? 0 : x.severity === 'high' ? -1 : 1) || KIND_ORDER[x.type] - KIND_ORDER[y.type] || x.package_name.localeCompare(y.package_name));
  const kc = (t) => devs.filter(d => d.type === t).length;
  const needs = a.status === 'none' || a.status === 'expired';
  const soon = w.left != null && w.left > 0 && w.left <= SOON_DAYS;
  const noSnapshot = !a.last_snapshot_at;

  const expLine = a.status === 'none' ? `Baselines last ${WINDOW_DAYS} days once set.`
    : w.left <= 0 ? `Expired ${Math.max(0, -w.left)} day${-w.left === 1 ? '' : 's'} ago — drift isn’t being checked.`
    : soon ? `Expires in ${w.left} day${w.left === 1 ? '' : 's'}. Renew it to keep checking drift.`
    : `Expires in ${w.left} days · ${stamp(a.baseline_expires_at)}`;

  const primaryLabel = a.status === 'drift' ? 'Accept as new baseline'
    : needs ? 'Set baseline'
    : soon ? 'Renew baseline' : 'Re-baseline';
  const primaryDisabled = !canEdit || !!busy || (a.status === 'waiting') || (needs && noSnapshot);

  let needsTitle = 'No baseline yet';
  let needsBody = noSnapshot
    ? 'No full snapshot has arrived from this agent yet, so there’s nothing to record. The agent sends one on its full-scan interval; check back after that.'
    : `Setting a baseline records the packages from the last full snapshot (${snapAgo}) as the known-good state. Anything installed, removed or changed after that shows up here as drift.`;
  if (a.status === 'expired') {
    needsTitle = `This baseline expired on ${stamp(a.baseline_expires_at, { year: undefined })}`;
    needsBody = `Baselines last ${WINDOW_DAYS} days, so ${a.hostname} hasn’t been checked for drift since then.${noSnapshot ? ' No snapshot is available to set a new one yet.' : ` Setting a new one uses its last full snapshot (${snapAgo}).`}${!online && !noSnapshot ? ' The agent is offline, so check the snapshot is still what you expect first.' : ''}`;
  }

  return (
    <aside aria-label="Baseline details" className="min-h-[420px] rounded-[14px] border flex flex-col overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
      <div className="flex flex-col gap-1.5 px-6 pt-5 pb-4 border-b" style={{ borderColor: 'var(--surf-line)' }}>
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[10.5px] tracking-[0.12em] truncate" style={{ color: 'var(--ink-link)' }}>AGENT{a.os_info ? ` · ${a.os_info.toUpperCase()}` : ''}</span>
          <button type="button" onClick={onClose} aria-label="Close details" className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
            <X className="w-[13px] h-[13px]" strokeWidth={2} />
          </button>
        </div>
        <h2 className="m-0 font-heading text-[22px] font-semibold tracking-[-0.01em] truncate">{a.hostname}</h2>
        <span className="flex items-center gap-2.5 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
          {a.ip_address && <span className="font-mono">{a.ip_address}</span>}
          <span className="flex items-center gap-1.5">
            <span className="w-[7px] h-[7px] rounded-full" style={{ background: online ? 'var(--sev-good-fg)' : 'var(--ink-faint)' }} />
            {online ? 'Agent online' : `Agent offline${a.last_seen ? ` · last heartbeat ${ago(a.last_seen)}` : ''}`}
          </span>
        </span>
      </div>

      <div className="flex-grow min-h-0 overflow-y-auto px-6 py-[18px] flex flex-col gap-4">
        <div className="flex flex-col gap-2.5 px-4 py-3.5 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
          <span className="flex justify-between items-baseline">
            <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>BASELINE</span>
            {a.package_count > 0 && <span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-muted)' }}>{a.package_count} packages</span>}
          </span>
          <span className="text-sm font-medium">{a.status === 'none' ? 'No baseline yet' : `Set ${stamp(a.baseline_set_at, { hour: '2-digit', minute: '2-digit', hour12: false })}`}</span>
          <WindowBar w={w} width="100%" height={6} />
          <span className="text-[12.5px]" style={{ color: soon || (w.left != null && w.left <= 0) || a.status === 'none' ? 'var(--ink-text)' : 'var(--ink-muted)' }}>{expLine}</span>
        </div>

        {a.status === 'drift' && (
          <div className="flex flex-col gap-2.5">
            <span className="flex justify-between">
              <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>CHANGED SINCE BASELINE</span>
              <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{devs.length} change{devs.length === 1 ? '' : 's'}</span>
            </span>
            <span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
              {kc('install')} installed · {kc('remove')} removed · {kc('downgrade')} downgraded · {kc('upgrade')} upgraded
            </span>
            {devs.map((d, i) => (
              <div key={`${d.ecosystem}:${d.package_name}:${i}`} className="flex flex-col gap-[5px] px-3 py-[9px] rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                <span className="flex items-center gap-2 min-w-0">
                  <Chip k={d.severity === 'high' ? 'high' : 'med'}>{d.severity === 'high' ? 'HIGH' : 'MEDIUM'}</Chip>
                  <span className="font-mono text-[13px] truncate">{d.package_name}</span>
                  <span className="font-mono text-[10.5px] tracking-[0.06em] shrink-0" style={{ color: 'var(--ink-muted)' }}>{d.type.toUpperCase()}</span>
                  <span className="ml-auto font-mono text-[10.5px] shrink-0" style={{ color: 'var(--ink-faint)' }}>{d.ecosystem}</span>
                </span>
                <span className="font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-muted)' }}>
                  {d.old_version ?? '(not installed)'} <span style={{ color: 'var(--ink-faint)' }}>→</span> {d.new_version ?? '(removed)'}
                </span>
              </div>
            ))}
            <span className="text-[11.5px] leading-normal" style={{ color: 'var(--ink-faint)' }}>
              Installs, removals and downgrades count as HIGH; upgrades as MEDIUM. Compared against the last full snapshot{snapAgo ? `, ${snapAgo}` : ''}.
            </span>
          </div>
        )}

        {needs && (
          <div className="flex flex-col gap-2.5 px-4 py-3.5 rounded-[10px] border border-dashed" style={{ borderColor: 'var(--surf-line-strong)' }}>
            <span className="text-sm font-semibold">{needsTitle}</span>
            <span className="text-[13px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>{needsBody}</span>
          </div>
        )}

        {(a.status === 'clean' || a.status === 'waiting') && (
          <div className="flex items-center gap-2.5 px-4 py-3.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
            <Dot color={a.status === 'clean' ? 'var(--sev-good-fg)' : null} dashed />
            <span className="leading-normal">
              {a.status === 'waiting'
                ? 'Baseline set, but there’s no current package list to compare it with. Drift can’t be checked yet — this isn’t the same as clean. Full snapshots are sent less often than the 15-minute heartbeat.'
                : `Matches its baseline. Last compared against the full snapshot from ${snapAgo || 'the last scan'}.`}
            </span>
          </div>
        )}
      </div>

      {confirming && (
        <div role="alertdialog" aria-label="Replace baseline" className="mx-6 mb-4 flex flex-col gap-2.5 px-4 py-3.5 rounded-[10px] border" style={{ borderColor: 'var(--accent)', background: 'var(--surf-raised)' }}>
          <span className="text-sm font-semibold">Replace the baseline for {a.hostname}?</span>
          <span className="text-[13px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
            {a.current_package_count ? `The ${a.current_package_count} packages in the latest snapshot become the new baseline` : 'The latest snapshot becomes the new baseline'}
            {devs.length ? `, and the ${devs.length} change${devs.length === 1 ? '' : 's'} above stop showing as drift` : ''}. The current baseline isn’t kept.
          </span>
          <span className="flex gap-2">
            <button type="button" onClick={onConfirm} disabled={!!busy} className="h-10 px-4 rounded-[9px] text-[13.5px] font-semibold disabled:opacity-50" style={{ background: 'var(--accent)', color: '#0A0A0A' }}>
              {busy === a.server_id ? 'Replacing…' : 'Replace baseline'}
            </button>
            <button type="button" onClick={onCancel} className="h-10 px-3.5 rounded-[9px] border text-[13.5px]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>Keep current</button>
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 px-6 py-4 border-t" style={{ borderColor: 'var(--surf-line)' }}>
        {canEdit ? (
          !confirming && (
            <button type="button" onClick={onPrimary} disabled={primaryDisabled} title={a.status === 'waiting' ? 'Waiting for a full snapshot' : needs && noSnapshot ? 'No snapshot yet' : undefined} className={primaryBtn} style={primaryDisabled ? { background: 'var(--surf-raised)', color: 'var(--ink-faint)' } : primaryBtnStyle}>
              {busy === a.server_id ? 'Setting…' : primaryLabel}
            </button>
          )
        ) : (
          <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>You can view baselines. Changing them needs the Edit assets permission.</span>
        )}
        <Link to={`/assets/${a.server_id}`} className="ml-auto h-11 flex items-center px-1 text-sm" style={{ color: 'var(--ink-link)' }}>Open asset</Link>
      </div>
    </aside>
  );
};

export default BaselineManagement;
